import { useEffect, useRef, useState } from "react";
import { turnstileSize } from "@/lib/turnstile-options";

declare global {
  interface Window {
    turnstile: {
      render: (container: HTMLElement, options: TurnstileOptions) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

interface TurnstileOptions {
  sitekey: string;
  callback: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void | boolean;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact";
}

interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact" | "invisible";
  className?: string;
}

const TURNSTILE_SITE_KEY = "0x4AAAAAAACFkW8M9eEDSt4Xd";

export const TurnstileWidget = ({
  onVerify,
  onExpire,
  onError,
  theme = "auto",
  size = "normal",
  className = "",
}: TurnstileWidgetProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Cloudflare shows its own error box (with a "Troubleshoot" link) when verification fails; hide it.
  const [hasError, setHasError] = useState(false);

  const callbackRef = useRef({ onVerify, onExpire, onError });
  callbackRef.current = { onVerify, onExpire, onError };

  useEffect(() => {
    let cancelled = false;
    let pollTimer: number | undefined;

    const renderWidget = () => {
      if (cancelled || !containerRef.current || !window.turnstile || widgetIdRef.current) return;

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => {
          setHasError(false);
          callbackRef.current.onVerify(token);
        },
        "expired-callback": () => callbackRef.current.onExpire?.(),
        "error-callback": () => {
          setHasError(true);
          callbackRef.current.onError?.();
          return true;
        },
        theme,
        size: turnstileSize(size),
      });
    };

    // Load Turnstile explicitly so it can be rendered reliably after route changes.
    const existingScript = document.querySelector<HTMLScriptElement>('script[data-linkzy-turnstile]');

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.linkzyTurnstile = "true";
      script.addEventListener("load", renderWidget, { once: true });
      document.head.appendChild(script);
    } else if (window.turnstile) {
      renderWidget();
    } else {
      pollTimer = window.setInterval(() => {
        if (window.turnstile) {
          window.clearInterval(pollTimer);
          renderWidget();
        }
      }, 100);
    }

    return () => {
      cancelled = true;
      if (pollTimer) window.clearInterval(pollTimer);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Widget may already have been removed.
        }
        widgetIdRef.current = null;
      }
    };
  }, [theme, size]);

  return <div ref={containerRef} className={className} hidden={hasError} aria-hidden={hasError} />;
};

export const resetTurnstile = (widgetId: string) => {
  if (window.turnstile && widgetId) {
    window.turnstile.reset(widgetId);
  }
};
